import React from "react";
import { Conf, EvidenceList, Unknown } from "./primitives";

const Th = ({ children }) => <th className="px-1.5 py-1 font-medium text-left whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "", testId }) => <td className={`px-1.5 py-1 align-top ${className}`} data-testid={testId}>{children}</td>;

/** Riepilogo assieme (S0 pacchetto): distinta, materiale/finitura globali, ruoli pagina, collegamenti. */
export const AssemblySummary = ({ d }) => {
  const p = d.merged.package;
  const s0 = d.segments.find((s) => s.segment_id === "S0");
  const notes = d.notes_by_segment.S0 || [];
  const s0raw = d.segment_raw_notes.S0 || {};
  return (
    <section className="border border-border bg-white" data-testid="result-assembly">
      <div className="px-2 py-1.5 border-b border-border bg-muted flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display font-bold text-sm">Riepilogo assieme</h2>
        <details className="text-[10px] text-muted-foreground" data-testid="result-assembly-provenance"><summary className="cursor-pointer" data-testid="result-assembly-provenance-toggle">Provenienza tecnica</summary><span className="font-mono break-all">S0 · {s0?.response_id} · {s0?.output_hash} · {s0?.evidence_count} evidenze</span></details>
      </div>
      <div className="p-2 space-y-2 text-[11px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
          <div>Tipo documento: <b data-testid="result-document-type">{p.document_type || <Unknown />}</b></div>
          <div>Pagine: <b data-testid="result-pages">{p.pages_received}/{p.pages_expected}</b>{p.missing_pages?.length ? <span className="ml-1 px-1 border border-red-400 bg-red-50 text-red-900 font-bold text-[9px]">mancante: {p.missing_pages.join(", ")}</span> : null}</div>
          <div className="flex flex-wrap items-center gap-1">Materiale globale: {p.material_global ? <><b data-testid="result-material-global">{p.material_global.value}</b><Conf value={p.material_global.confidence} /><span className="text-[9px] text-muted-foreground">{p.material_global.origin}</span><EvidenceList segId="S0" ids={p.material_global.evidence_ids} /></> : <Unknown testId="result-material-global" />}</div>
          <div className="flex flex-wrap items-center gap-1">Finitura globale: {p.finish_global ? <><b data-testid="result-finish-global">{p.finish_global.value}</b><Conf value={p.finish_global.confidence} /><span className="text-[9px] text-muted-foreground">{p.finish_global.origin}</span><EvidenceList segId="S0" ids={p.finish_global.evidence_ids} /></> : <Unknown testId="result-finish-global" />}</div>
        </div>

        <div className="overflow-x-auto border border-border">
          <table className="w-full text-[11px]" data-testid="result-bom-table">
            <thead><tr className="bg-muted border-b border-border"><Th>#</Th><Th>Codice</Th><Th>Q.tà (disegno)</Th><Th>Materiale (distinta)</Th><Th>Spessore</Th><Th>Pag. dettaglio</Th><Th>Sviluppo</Th><Th>Evidenze</Th></tr></thead>
            <tbody>
              {p.bom_rows.map((r) => {
                const link = p.component_links.find((l) => l.code === r.code);
                const flat = p.flat_links.find((l) => l.code === r.code);
                return (
                  <tr key={r.row_id} className="border-b border-border" data-testid={`result-bom-row-${r.row_id}`}>
                    <Td>{r.row_id}</Td>
                    <Td className="font-mono" testId={`result-bom-code-${r.row_id}`}>{r.code}</Td>
                    <Td testId={`result-bom-qty-${r.row_id}`}>{r.qty_from_drawing ?? <Unknown />}</Td>
                    <Td testId={`result-bom-material-${r.row_id}`}>{r.material || <Unknown />}</Td>
                    <Td testId={`result-bom-thickness-${r.row_id}`}>{r.thickness_mm != null ? `${r.thickness_mm} mm` : <Unknown />}</Td>
                    <Td>{link ? <>p.{link.detail_page} <EvidenceList segId="S0" ids={link.evidence_ids} /></> : "—"}</Td>
                    <Td>{flat ? `piegato p.${flat.formed_page} · sviluppo p.${flat.development_page}` : "—"}</Td>
                    <Td><EvidenceList segId="S0" ids={r.evidence_ids} /></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-1" data-testid="result-page-roles">
          {p.page_roles.map((pr) => (
            <span key={pr.page} className="px-1.5 py-0.5 border border-border text-[10px]">p.{pr.page}: {pr.roles.join(" + ")} <EvidenceList segId="S0" ids={pr.evidence_ids} /></span>
          ))}
          {(p.missing_pages || []).map((mp) => <span key={mp} className="px-1.5 py-0.5 border border-red-400 bg-red-50 text-red-900 text-[10px] font-bold">p.{mp}: NON RICEVUTA</span>)}
        </div>

        {(notes.length > 0 || (s0raw.open_questions || []).length > 0) && (
          <div className="border border-border p-1.5 space-y-0.5" data-testid="result-s0-notes">
            {notes.map((n) => <div key={n.note_id} className="text-[10px]"><span className="font-mono">{n.note_id}</span> {n.text}</div>)}
            {(s0raw.open_questions || []).map((q, i) => <div key={i} className="text-[10px] text-amber-900"><b>Domanda aperta S0:</b> {q}</div>)}
          </div>
        )}
      </div>
    </section>
  );
};
